import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Check,
  ChevronRight,
  FileOutput,
  FileText,
  PenLine,
  Search,
  Settings2,
  Users,
} from "lucide-react";
import { getDraftStepHref } from "@/lib/draft-step-nav";
import { cn } from "@/lib/utils";
import { DRAFT_WIZARD_STEPS } from "@/types/drafting";

const STEP_ICONS = [Settings2, Users, PenLine, Search, FileOutput];

interface DraftStepperProps {
  currentStep: number;
  matterId?: string;
  className?: string;
}

export function DraftStepper({ currentStep, matterId, className }: DraftStepperProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const activeStepRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scroller = scrollerRef.current;
    const active = activeStepRef.current;
    if (!scroller || !active) return;
    const nextLeft =
      active.offsetLeft - (scroller.clientWidth - active.offsetWidth) / 2;
    scroller.scrollTo({
      left: Math.max(0, nextLeft),
      behavior: "smooth",
    });
  }, [currentStep]);

  return (
    <div
      ref={scrollerRef}
      className={cn(
        "draft-stepper-scroll w-full min-w-0 overflow-x-auto overscroll-x-contain pb-1",
        className
      )}
      role="navigation"
      aria-label="Draft steps"
    >
      <div className="flex w-max min-w-full items-center justify-center gap-2 sm:gap-3">
        {DRAFT_WIZARD_STEPS.map((step, index) => {
          const Icon = STEP_ICONS[index] ?? FileText;
          const isActive = step.id === currentStep;
          const isCompleted = step.id < currentStep;
          const href = getDraftStepHref(matterId, step.id);
          const isUpcoming = Boolean(href) && !isActive && !isCompleted;
          const isLast = index === DRAFT_WIZARD_STEPS.length - 1;

          const content = (
            <>
              <div
                className={cn(
                  "flex h-9 w-9 items-center justify-center rounded-full transition-all sm:h-10 sm:w-10",
                  isActive
                    ? "bg-primary text-primary-foreground shadow-lg shadow-primary/30"
                    : isCompleted
                      ? "bg-[var(--tint-green-bg)] text-[#027a48] dark:bg-green-500/20 dark:text-green-300"
                      : "bg-[var(--surface-sunken)] text-[var(--lm-muted)] dark:bg-slate-800 dark:text-slate-400"
                )}
              >
                {isCompleted ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
              </div>
              <div className="hidden text-left sm:block">
                <div
                  className={cn(
                    "text-xs font-semibold sm:text-sm",
                    isActive
                      ? "text-[var(--lm-foreground-strong)] dark:text-white"
                      : "text-[var(--lm-muted)] dark:text-slate-400"
                  )}
                >
                  {step.title}
                </div>
                <div className="text-[10px] text-[var(--lm-muted)] dark:text-slate-500">
                  {step.description}
                </div>
              </div>
            </>
          );

          return (
            <div
              key={step.id}
              ref={isActive ? activeStepRef : undefined}
              className="flex shrink-0 items-center"
            >
              {href ? (
                <Link
                  to={href}
                  className={cn(
                    "flex items-center gap-2 rounded-xl px-2 py-2 transition-all sm:gap-3 sm:px-3",
                    isActive
                      ? "border border-[var(--primary-soft-border)] bg-[var(--primary-soft-bg)] dark:border-primary/50 dark:bg-primary/20"
                      : isCompleted
                        ? "border border-transparent hover:bg-[var(--lm-hover)] dark:hover:bg-slate-800/50"
                        : isUpcoming
                          ? "border border-transparent hover:border-[var(--lm-border-subtle)] hover:bg-[var(--lm-hover)] dark:hover:border-slate-700 dark:hover:bg-slate-800/50"
                          : "border border-transparent hover:bg-[var(--lm-hover)] dark:hover:bg-slate-800/50"
                  )}
                >
                  {content}
                </Link>
              ) : (
                <div
                  className={cn(
                    "flex items-center gap-2 rounded-xl px-2 py-2 sm:gap-3 sm:px-3",
                    isActive
                      ? "border border-[var(--primary-soft-border)] bg-[var(--primary-soft-bg)] dark:border-primary/50 dark:bg-primary/20"
                      : "opacity-60"
                  )}
                >
                  {content}
                </div>
              )}
              {!isLast ? (
                <ChevronRight
                  className={cn(
                    "mx-0.5 h-4 w-4 shrink-0 sm:mx-1",
                    isCompleted
                      ? "text-primary/70 dark:text-primary/80"
                      : "text-[var(--lm-muted)] dark:text-slate-500"
                  )}
                  strokeWidth={2}
                  aria-hidden
                />
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
